import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Instagram } from "lucide-react";
import { getGeckos, getInstagramMedia, getSettings, HERO_BG, scrollToSection } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Velkommen til", "GEKKOFUEL."];

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.25], ["0%", "16%"]);
  const [images, setImages] = useState({ hero_bg: HERO_BG, hero_portrait: "" });
  const [photos, setPhotos] = useState([]);
  const [photoIdx, setPhotoIdx] = useState(0);

  useEffect(() => {
    getSettings()
      .catch(() => ({}))
      .then((s) =>
        setImages({ hero_bg: s.hero_bg || HERO_BG, hero_portrait: s.hero_portrait || "" })
      );
    // Instagram- og gecko-kald kører uafhængigt, så et langsomt Instagram-svar ikke blokerer kortene
    getInstagramMedia()
      .catch(() => [])
      .then((ig) => {
        const igPhotos = ig.map((p) => p.media_url).filter(Boolean);
        if (igPhotos.length) {
          setPhotos((prev) => (prev.length ? prev : igPhotos));
        }
      });
    getGeckos()
      .catch(() => [])
      .then((geckos) => {
        setPhotos(
          geckos.flatMap((g) => (g.images?.length ? g.images : g.image ? [g.image] : []))
        );
      });
  }, []);

  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setPhotoIdx((i) => (i + 1) % photos.length), 4500);
    return () => clearInterval(t);
  }, [photos.length]);

  const caption = (
    <a
      data-testid="hero-slideshow-caption"
      href="https://www.instagram.com/gekkofuel"
      target="_blank"
      rel="noopener noreferrer"
      className="absolute bottom-0 z-10 flex w-full items-center gap-2 bg-ink/70 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-pale backdrop-blur-md transition-colors hover:text-cream"
    >
      <Instagram className="h-3.5 w-3.5" /> Nyeste fra opdrættet — @gekkofuel
    </a>
  );

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.img
        src={images.hero_bg}
        alt="Mørke skovbregner"
        style={{ y: bgY }}
        className="absolute inset-0 h-[115%] w-full object-cover opacity-35"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/70" />

      {photos.length > 0 && (
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          className="absolute bottom-32 right-16 hidden w-[380px] overflow-hidden border border-white/10 xl:block"
        >
          <a
            data-testid="hero-instagram-image-link"
            href="https://www.instagram.com/gekkofuel"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div data-testid="hero-slideshow" className="relative h-[460px] w-full">
            <AnimatePresence mode="sync">
              <motion.img
                key={photos[photoIdx]}
                src={photos[photoIdx]}
                alt="Kronegekko fra GEKKOFUEL-opdrættet"
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
              />
            </AnimatePresence>
            </div>
          </a>
          {caption}
        </motion.div>
      )}

      <div className="relative z-10 w-full px-6 pb-24 pt-40 lg:px-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 border-l-2 border-forest pl-4 font-mono text-xs uppercase tracking-[0.25em] text-pale"
        >
          Hobbyopdræt · Frederik &amp; Sesilie · Danmark
        </motion.p>

        <h1 className="max-w-4xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: EASE }}
                className={`block font-serif text-5xl leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl ${
                  i === 1 ? "notranslate italic text-pale" : "text-cream"
                }`}
                translate={i === 1 ? "no" : undefined}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-6 font-serif text-2xl italic tracking-tight text-pale sm:text-3xl"
        >
          Slip din vilde side fri.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-sage sm:text-lg"
        >
          Kronegekkoer i nøje udvalgte morfer, det foder og den jord, vi selv
          stoler på — og håndbyggede terrarium-baggrunde.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollToSection("#morphs")}
            className="group flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
          >
            Se alle Correlophus ciliatus
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-enquiry-button"
            onClick={() => scrollToSection("#enquiry")}
            className="group flex items-center gap-3 rounded-full border border-white/25 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-pale hover:text-pale"
          >
            Skriv til os
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 font-mono text-[10px] uppercase tracking-[0.25em] text-sage/70"
        >
          Gratis fragt ved køb over 499 kr · op til 10 kg
        </motion.p>

        {photos.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="relative mt-12 overflow-hidden border border-white/10 xl:hidden"
          >
            <a
              data-testid="hero-instagram-image-link-mobile"
              href="https://www.instagram.com/gekkofuel"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div data-testid="hero-slideshow-mobile" className="relative h-64 w-full">
              <AnimatePresence mode="sync">
                <motion.img
                  key={photos[photoIdx]}
                  src={photos[photoIdx]}
                  alt="Kronegekko fra GEKKOFUEL-opdrættet"
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: EASE }}
                />
              </AnimatePresence>
              </div>
            </a>
            {caption}
          </motion.div>
        )}
      </div>
    </section>
  );
}
